import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Grid,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import toast from "react-hot-toast";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { post } from "../apis/apis";
import CircularProgress from "@mui/material/CircularProgress";

const SignersDialogTemplate = ({
  open,
  onClose,
  signers,
  setSigners,
  signerToEdit,
  file_id,
  setAnnotationsd,
  annotations,
  templateDoc,
  EditingTemp,
  primaryColorComp,
}) => {
  const [name, setName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [email, setEmail] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [error, setError] = useState({});
  const [deletingSignerIndex, setDeletingSignerIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedSignerToDelete, setSelectedSignerToDelete] = useState(null);
  const handleDeleteClick = (index) => {
    setSelectedSignerToDelete(index);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const index = selectedSignerToDelete;
    if (index === null) return;
    if (signers.length === 1) {
      toast.error("You can't delete the last signer");
      return;
    }
    setDeletingSignerIndex(index);

    try {
      const signerToDelete = signers[index];
      if (!signerToDelete.signer_id) {
        toast.error("Invalid signer ID");
        return;
      }
      console.log(signerToDelete);

      const postData = {
        signer_id: signerToDelete.signer_id,
      };

      const apiData = await post("file/delete-signer", postData);

      if (apiData.error) {
        toast.error(apiData.message || "Failed to delete signer");
        return;
      }
      console.log("deleted signer ");

      const updatedSigners = signers.filter((_, i) => i !== index);
      const signersWithUpdatedColors = updatedSigners.map(
        (signer, newIndex) => ({
          ...signer,
          color: colors[newIndex],
          order_id: (newIndex + 1).toString(),
        })
      );

      setSigners(signersWithUpdatedColors);
      const newAnnotations = {};
      Object.entries(annotations).forEach(([pageNum, pageAnnotations]) => {
        if (pageNum === "_lastUpdate" || !Array.isArray(pageAnnotations)) return;
        const filteredAnnotations = pageAnnotations.filter(
          (annot) =>
            annot.signerId !== signerToDelete.signer_id &&
            annot.signer_id !== signerToDelete.signer_id
        );
        if (filteredAnnotations.length > 0) {
          newAnnotations[pageNum] = filteredAnnotations;
        }
      });
      setAnnotationsd(newAnnotations);

      toast.success("Signer deleted successfully");
      await updateSignerOrder(signersWithUpdatedColors);
      setName("");
      setEmail("");
      setAccessCode("");
      setEditingIndex(null);
      setError({});
    } catch (error) {
      console.error("Error deleting signer:", error);
      toast.error("Error deleting signer");
    } finally {
      setDeletingSignerIndex(null);
      setDeleteConfirmOpen(false);
      setSelectedSignerToDelete(null);
    }
  };
  const generateSignerId = () => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  };
  const onDragEnd = (result) => {
    if (!result.destination || !signers.length) return;

    const items = Array.from(signers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update colors and order_id based on new positions
    const updatedSigners = items.map((signer, index) => ({
      ...signer,
      color: colors[index],
      order_id: index + 1, // Add order_id starting from 1
    }));

    setSigners(updatedSigners);

    // Call API to update order
    updateSignerOrder(updatedSigners);
  };

  useEffect(() => {
    if (signerToEdit) {
      setName(signerToEdit.signer.name);
      setEmail(signerToEdit.signer.email);
      setAccessCode(signerToEdit.signer.access_code || "");
      setEditingIndex(signerToEdit.index);
    }
  }, [signerToEdit]);
  useEffect(() => {
    if (templateDoc === true) {
      setName(`Signer ${signers.length + 1}`);
    }
  }, [signers]);
  const colors = [
    "rgb(255 226 139 / 98%)",
    "rgb(192 165 207 / 98%)",
    "rgb(151 201 191 / 98%)",
    "rgb(247 185 148 / 98%)",
    "rgb(207 219 127 / 98%)",
    "rgb(255 153 128 / 98%)",
    "rgb(255 179 198 / 98%)",
    "rgb(230 198 230 / 98%)",
  ];
  const validateForm = () => {
    const newError = {};

    if (!name.trim()) newError.name = "Name is required";
    if (EditingTemp === false) {
      if (!email.trim()) {
        newError.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newError.email = "Invalid email format";
      } else {
        // Check for duplicate email
        const isDuplicate = signers.some((signer, index) => {
          // If we're editing, exclude the current signer from the check
          if (editingIndex !== null && index === editingIndex) {
            return false;
          }
          return signer.email.toLowerCase() === email.toLowerCase();
        });

        if (isDuplicate) {
          newError.email = "This email is already assigned to another signer";
        }
      }
    }

    // Only validate access code if it's not empty
    // Optional access code validation - only check if there's a value

    setError(newError);
    return Object.keys(newError).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    if (signers.length >= 8 && editingIndex === null) {
      toast.error("Maximum 8 signers allowed");
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedSignersList = [...signers];

      if (editingIndex !== null) {
        // Edit existing signer
        const updatedSigner = {
          ...updatedSignersList[editingIndex],
          name,
          email,
          access_code: accessCode,
          color: colors[editingIndex],
          // Preserve the existing signer_id
          signer_id: updatedSignersList[editingIndex].signer_id,
        };
        updatedSignersList[editingIndex] = updatedSigner;
      } else {
        // Add new signer
        const newSigner = {
          name,
          email,
          access_code: accessCode,
          color: colors[signers.length % colors.length],
          order_id: signers.length + 1,
          // No signer_id for new signers
        };
        updatedSignersList = [...signers, newSigner];
      }

      const apiData = {
        file_id: file_id,
        signers: updatedSignersList.map((signer) => ({
          name: signer.name,
          email: signer.email,
          access_code: signer.access_code,
          order_id: signer.order_id,
          color: signer.color,
          signer_id: signer.signer_id, // Will be undefined for new signers
        })),
      };

      const response = await post("file/add-signer-editor", apiData);

      if (response.error) {
        toast.error(response.message || "Failed to update signers");
        return;
      }

      // Update state with response data
      setSigners(response.data);

      toast.success(
        editingIndex !== null
          ? "Signer updated successfully"
          : "Signer added successfully"
      );
      clearForm();
      // onClose();
    } catch (error) {
      console.error("Error handling signer:", error);
      toast.error("An error occurred while processing your request");
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSubmit1 = async () => {
    if (!validateForm()) return;

    if (signers.length >= 8 && editingIndex === null) {
      toast.error("Maximum 8 signers allowed");
      return;
    }

    try {
      if (editingIndex !== null) {
        // Edit existing signer
        const updatedSigners = [...signers];
        const signerToUpdate = {
          ...updatedSigners[editingIndex],
          name,
          email,
          access_code: accessCode,
          color: colors[editingIndex],
        };

        // API call to update signer
        const updateData = {
          signer_id: signerToUpdate.id,
          name: signerToUpdate.name,
          email: signerToUpdate.email,
          access_code: signerToUpdate.access_code,
          order_id: signerToUpdate.order_id,
        };

        const updateResponse = await post("file/add-signer", updateData);

        if (updateResponse.error) {
          toast.error(updateResponse.message || "Failed to update signer");
          return;
        }

        updatedSigners[editingIndex] = signerToUpdate;
        setSigners(updatedSigners);
        toast.success("Signer updated successfully");
      } else {
        // Add new signer
        const newSigner = {
          id: generateSignerId(),
          name,
          email,
          access_code: accessCode,
          color: colors[signers.length % colors.length],
          order_id: signers.length + 1,
        };

        // API call to add signer
        const addData = {
          name: newSigner.name,
          email: newSigner.email,
          access_code: newSigner.access_code,
          order_id: newSigner.order_id,
          color: newSigner.color,
        };

        const addResponse = await post("file/add-signer", addData);

        if (addResponse.error) {
          toast.error(addResponse.message || "Failed to add signer");
          return;
        }

        // Use the ID from the API response if provided
        const signerWithId = {
          ...newSigner,
          id: addResponse.data?.id || newSigner.id,
        };

        setSigners([...signers, signerWithId]);
        toast.success("Signer added successfully");
      }

      clearForm();
    } catch (error) {
      console.error("Error handling signer:", error);
      toast.error("An error occurred while processing your request");
    }
  };
  const handleEdit = (index) => {
    const signer = signers[index];
    setName(signer.name);
    setEmail(signer.email);
    setAccessCode(signer.access_code);
    setEditingIndex(index);
  };
  // Add function to update order in backend
  const updateSignerOrder = async (updatedSigners) => {
    try {
      const postData = {
        signers: updatedSigners.map((signer) => ({
          signer_id: signer.signer_id,
          color: signer.color,
          order_id: signer.order_id,
        })),
      };
      console.log(postData);
      const apiData = await post("file/update-signer-order", postData);
      console.log("uodate signer ", apiData);
      if (apiData.error) {
        toast.error("Failed to update signer order");
      }
    } catch (error) {
      console.error("Error updating signer order:", error);
      toast.error("Error updating signer order");
    }
  };

  const clearForm = () => {
    setName("");
    setEmail("");
    setAccessCode("");
    setEditingIndex(null);
    setError({});
  };
  // Update onClose handler
  const handleClose = () => {
    clearForm();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        PaperProps={{
          sx: {
            height: "80vh", // Set maximum height
            maxHeight: "80vh",
          },
        }}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Signers
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5} style={{ marginTop: "10px" }}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Name *
                </Typography>
                <TextField
                  fullWidth
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  error={!!error.name}
                  helperText={error.name}
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      height: "40px", // Add this
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 14px", // Add this
                    },
                    "& .MuiFormHelperText-root": {
                      marginTop: "4px", // Add this
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />

                {EditingTemp ? (
                  <></>
                ) : (
                  <>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 1,
                        color: "text.secondary",
                        fontWeight: 500,
                      }}
                    >
                      Email *
                    </Typography>
                    <TextField
                      fullWidth
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      error={!!error.email}
                      helperText={error.email}
                      sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "white",
                          height: "40px", // Add this
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "8px 14px", // Add this
                        },
                        "& .MuiFormHelperText-root": {
                          marginTop: "4px", // Add this
                        },
                      }}
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </>
                )}

                {/* <Typography
                  variant="body2"
                  sx={{
                    mb: 1,
                    color: "text.secondary",
                    fontWeight: 500,
                  }}
                >
                  Access Code (Requirement 6 digits)
                </Typography>
                <TextField
                  fullWidth
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.slice(0, 6))}
                  error={!!error.accessCode}
                  helperText={error.accessCode}
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                      height: "40px", // Add this
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "8px 14px", // Add this
                    },
                    "& .MuiFormHelperText-root": {
                      marginTop: "4px", // Add this
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 6 }}
                  variant="outlined"
                /> */}
              </Box>

              <Button
                variant="contained"
                disabled={isSubmitting}
                onClick={handleSubmit}
                sx={{
                  marginTop: 0,
                  boxShadow: "none",
                  bgcolor:
                    editingIndex !== null
                      ? "#28c76f"
                      : primaryColorComp === null ||
                        primaryColorComp === undefined
                      ? "rgb(35, 179, 232)"
                      : primaryColorComp,
                  "&:hover": {
                    boxShadow: "none",
                    bgcolor:
                      editingIndex !== null
                        ? "#17aa59"
                        : primaryColorComp === null ||
                          primaryColorComp === undefined
                        ? "#0085b5"
                        : primaryColorComp,
                  },
                  "&.Mui-disabled": {
                    bgcolor:
                      editingIndex !== null
                        ? "rgba(40, 199, 111, 0.7)"
                        : primaryColorComp === null ||
                          primaryColorComp === undefined
                        ? "rgba(35, 179, 232, 0.7)"
                        : primaryColorComp,
                    color: "white",
                  },
                  minWidth: "120px", // Ensure consistent width
                  position: "relative", // For loader positioning
                  minHeight: "40px",
                }}
              >
                {isSubmitting ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "white",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                ) : editingIndex !== null ? (
                  "Update Signer"
                ) : (
                  "Add Signer"
                )}
              </Button>
            </Grid>
            <Grid item xs={12} sm={7}>
              <Typography variant="h6">
                Signers List ({signers.length}/8)
              </Typography>

              <List
                sx={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  "& ::-webkit-scrollbar": { width: 6 },
                  "& ::-webkit-scrollbar-thumb": {
                    backgroundColor: "#ccc",
                    borderRadius: 3,
                  },
                }}
              >
                {signers.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "200px",
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                      borderRadius: "8px",
                      border: "1px dashed rgba(0, 0, 0, 0.12)",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.secondary",
                        textAlign: "center",
                        mb: 1,
                      }}
                    >
                      No signers added yet
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.disabled",
                        textAlign: "center",
                      }}
                    >
                      Add signers using the form on the left
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {signers.map((signer, index) => (
                        <ListItem
                          key={signer.signer_id || signer.id || `${signer.email || signer.name || "signer"}-${index}`}
                          sx={{
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            // background: snapshot.isDragging
                            //   ? "rgba(0, 0, 0, 0.02)"
                            //   : "white",
                            "&:hover": {
                              background: "rgba(0, 0, 0, 0.02)",
                            },
                          }}
                        >
                          <div
                            style={{
                              backgroundColor: signer.color || colors[index],
                              marginRight: "10px",
                              width: EditingTemp ? "40px" : "40px",
                              height: EditingTemp ? "40px" : "40px",
                              borderRadius: "50%",
                              flexShrink: 0,
                            }}
                          />
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.secondary" }}
                                >
                                  #{signer.order_id}
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {signer.name}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              EditingTemp ? null : (
                                <>
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography
                                      component="span"
                                      variant="body2"
                                    >
                                      {signer.email}
                                    </Typography>
                                    <br />
                                  </Box>
                                </>
                              )
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              onClick={() => handleEdit(index)}
                            >
                              <EditIcon />
                            </IconButton>
                            {EditingTemp ? (
                              <IconButton
                                edge="end"
                                onClick={() => handleDeleteClick(index)}
                                disabled={deletingSignerIndex === index}
                                sx={{
                                  color: "#ea5455",
                                  position: "relative",
                                }}
                              >
                                {deletingSignerIndex === index ? (
                                  <CircularProgress
                                    size={24}
                                    sx={{
                                      color: "#ea5455",
                                      position: "absolute",
                                      top: "50%",
                                      left: "50%",
                                      marginTop: "-12px",
                                      marginLeft: "12px",
                                    }}
                                  />
                                ) : (
                                  <DeleteIcon />
                                )}
                              </IconButton>
                            ) : null}

                            {/* <IconButton
                                      edge="end"
                                      onClick={() => handleDeleteClick(index)}
                                      disabled={deletingSignerIndex === index}
                                      sx={{
                                        color: "#ea5455",
                                        position: "relative",
                                      }}
                                    >
                                      {deletingSignerIndex === index ? (
                                        <CircularProgress
                                          size={24}
                                          sx={{
                                            color: "#ea5455",
                                            position: "absolute",
                                            top: "50%",
                                            left: "50%",
                                            marginTop: "-12px",
                                            marginLeft: "12px",
                                          }}
                                        />
                                      ) : (
                                        <DeleteIcon />
                                      )}
                                    </IconButton> */}
                          </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                  </>
                )}
                {/* {provided.placeholder} */}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              color: "white",
              fontWeight: 600,
              backgroundColor:
                primaryColorComp === null || primaryColorComp === undefined
                  ? "#21b3e8"
                  : primaryColorComp,
              boxShadow: "none",
              position: "relative", // Add this for loader positioning
              "&:hover": {
                boxShadow: "none",
                backgroundColor:
                  primaryColorComp === null || primaryColorComp === undefined
                    ? "#00bcff"
                    : primaryColorComp,
              },
              // "&.Mui-disabled": {
              //   backgroundColor: "rgba(249, 164, 77, 0.7)",
              //   color: "white",
              // },
            }}
          >
            Save & Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add this inside your main Dialog component, before the closing tag */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this signer? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            sx={{
              color: "text.secondary",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            sx={{
              bgcolor: "#ea5455",
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#d63031",
                boxShadow: "none",
              },
            }}
          >
            <CircularProgress
              size={20}
              color="inherit"
              sx={{
                display:
                  deletingSignerIndex === selectedSignerToDelete
                    ? "block"
                    : "none",
              }}
            />
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignersDialogTemplate;
